import { observer } from 'mobx-react';
import { useEffect, useMemo, MouseEvent } from 'react';
import { useMedia, useStore } from 'shared/hooks';
import { IUserDashboardWidgetItemModel, IUserDashboardGroup, UserDashboardWidgetModel } from 'shared/models';
import { PPDWidgetBonusBadge } from '../PPDWidgetBonusBadge';
import { PPDWidgetStatsTable } from './components/PPDWidgetStatsTable'; // Таблица плановых показателей
import { PPDWidgetBonusModal } from '../PPDWidgetBonusModal';
import { PPDWidgetBaseHeader } from '../PPDWidgetBaseHeader';
import { PPDWidgetBaseProperties } from '../PPDWidgetBaseProperties';
import { PPDWidgetStoresList } from '../PPDWidgetStoresList';
import { PPDWidgetGroupSelect } from '../PPDWidgetGroupSelect';
import { PPDWidgetStoresModal } from '../PPDWidgetStoresModal';
import { profileDashboardStore } from './../../../../store/ProfileDashBoardStore';
import classnames from 'classnames';
import { useDashboardDict } from './../../../../context/useDashboardDict';
import { PPDWidgetPropertiesBadge } from './components/PPDWidgetPropertiesBadge';
import { PPDWidgetCompareModal } from './components/PPDWidgetCompareModal';
import { PPDWidgetCompareChart } from "./components/PPDWidgetCompareChart";

import './styles.scss';
import { getMonoGroupId, isMonoStore } from 'pages/profile-dashboard/utilities/isMonoStore.ts';
import { UserDashboardGroupType } from 'shared/models/UserDashboardModel.ts';

type PropsType = {
    widget: UserDashboardWidgetModel
};

export const PPDWidgetCompare = observer(({ widget }: PropsType) => {
    const store = useStore(() => ({
        groupId: null as number | string | null,
        storeId: null as number | string | null,
        itemGroups: [] as IUserDashboardGroup[],
        positionId: null as number | string | null,
        selectedItem: null as null | IUserDashboardWidgetItemModel,
        isOpenStoresModal: false,
        isOpenContent: false,
        isOpenBonusModal: false,
        isExpandedList: false,
    }));

    const { stores = [], positions = [], groups = {} } = useDashboardDict() || {};

    const MAX_VISIBLE_ITEMS = 3;
    const { isMobileOrTablet } = useMedia();

    useEffect(() => {
        const type = widget?.items_group_type;
        if (!groups || !type) {
            store.set('itemGroups', []);
            return;
        }
        const allGroups = groups[type as keyof typeof groups] || [];
        const filteredGroups = allGroups.filter(g =>
            (widget.items || []).some(i => i.group_id === g.id),
        );
        store.set('itemGroups', filteredGroups);

        if (!store.groupId && filteredGroups.length) {
            store.set('groupId', filteredGroups[0].id);
        }
    }, [groups, store, widget?.items_group_type, widget.items]);

    useEffect(() => {
        if (!store.positionId && positions.length) {
            store.set('positionId', positions[0].id);
        }
    }, [positions, store]);

    const hasTable = useMemo(() => widget?.stores_view === 'list', [widget]);

    const item = useMemo(() => {
        const itemsSafe = widget.items || [];
        if (store.itemGroups.length > 0 && store.groupId) {
            return itemsSafe.find(i => i.group_id === store.groupId && i.store_id === null) ?? null;
        }
        return itemsSafe.find(i => i.store_id === null) ?? null;
    }, [store.groupId, store.itemGroups.length, widget.items]);

    const toggleExpanded = () => {
        isMobileOrTablet ? store.toggle('isExpandedList') : profileDashboardStore.toggleExpandedAllLists();
    };

    const isExpanded = isMobileOrTablet ? store.isExpandedList : profileDashboardStore.isExpandedAllLists;

    const filteredItems = useMemo(() => {
        const itemsSafe = widget.items || [];
        if (store.itemGroups.length > 0 && store.groupId) {
            return itemsSafe.filter(i => i.group_id === store.groupId && i.store_id !== null);
        }
        return itemsSafe.filter(i => i.store_id !== null);
    }, [store.groupId, store.itemGroups.length, widget.items]);

    const visibleItems =
        useMemo(() => isExpanded
            ? filteredItems
            : filteredItems.slice(0, MAX_VISIBLE_ITEMS),
        [isExpanded, filteredItems]);

    /**
     * ===========================================
     */
    const selectedItem = store.selectedItem;

    // 1) Уникальные id групп для выбранного store_id
    const selectedGroupIds = Array.from(
        new Set(
            (widget.items ?? [])
                .filter(i => i.store_id === selectedItem?.store_id && i.group_id != null)
                .map(i => i.group_id as string | number),
        ),
    );

    // 2) Объекты групп {id,name} из store.itemGroups по этим id
    const selectedGroupItems = (store.itemGroups ?? []).filter(g =>
        selectedGroupIds.includes(g.id),
    );

    const onChangeGroupInStoreModal = (groupId: number | string) => {
        store.set("groupId", groupId);

        const newItem = widget.items.find(
            i => i.group_id === groupId && i.store_id  === store.selectedItem?.store_id
        ) ?? null;

        store.set("selectedItem", newItem);
    };

    const getBadgeStatistic = useMemo(() => {
        if (!widget?.items) return [];

        // Получаем первые 3 элемента с store_id = null
        const nullStoreItems = widget.items
            .filter(item => item.store_id === null);

        // Определяем активную группу
        const activeGroupId = store.groupId || nullStoreItems[0]?.group_id;

        // Находим элемент с активной group_id
        const activeItem = nullStoreItems.find(item => item.group_id === activeGroupId);

        // Возвращаем statistic активного элемента или первого элемента
        return activeItem?.statistic || nullStoreItems[0]?.statistic || [];
    }, [widget?.items, store.groupId]);
    /**
     * ===========================================
     */

    if (!item) return null;

    return (
        <>
            <div className="ppd-widget-compare">
                <PPDWidgetBaseHeader
                    name={widget.name || '-'}
                    isOpen={store.isOpenContent}
                    onToggle={() => store.toggle('isOpenContent')}
                    isShowOptions={stores.length > 0 && widget?.stores_view === 'modal'}
                    onClickOptions={(event: MouseEvent) => {
                        event.stopPropagation();
                        store.set('isOpenStoresModal', true);
                    }}
                />
                {(!isMobileOrTablet || store.isOpenContent) && (
                    <>
                        <div className="ppd-widget-compare__content">
                            <div className="ppd-widget-compare__box">
                                {store.itemGroups.length > 0 && store.groupId && (
                                    <PPDWidgetGroupSelect
                                        items={store.itemGroups}
                                        value={store.groupId}
                                        onChange={(id) => store.set('groupId', id)}
                                    />
                                )}

                                {!!widget?.bonuses?.items?.length && (
                                    <div className="ppd-widget-compare__bonus-badge">
                                        <PPDWidgetBonusBadge
                                            isActive={widget?.bonuses?.status_id === 'received'}
                                            onClick={() => store.set('isOpenBonusModal', true)}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="ppd-widget-compare__wrapper">
                                <PPDWidgetCompareChart
                                    size={isMobileOrTablet ? 140 : 182}
                                    progress={item.value?.amount ?? 0}
                                />
                            </div>

                            <PPDWidgetBaseProperties
                                item={item}
                                labels={widget.labels || {}}
                                className="ppd-widget-compare__properties"
                            >
                                {(getBadgeStatistic.length > 0) && (
                                    <PPDWidgetPropertiesBadge
                                        statistic={getBadgeStatistic}
                                        label_full={widget?.labels?.statistics_full}
                                        label_short={widget?.labels?.statistics_short}
                                        zIndexTooltip={90}
                                    />
                                )}
                            </PPDWidgetBaseProperties>
                        </div>

                        {hasTable && visibleItems.length > 0 && (
                            <div className="ppd-widget-compare__bottom">
                                {isMobileOrTablet ? (
                                    <PPDWidgetStoresList
                                        items={visibleItems}
                                        onSelect={row => store.set('selectedItem', row)}
                                        valueType="detailed"
                                        withDirection={true}
                                    />
                                ) : (
                                    <PPDWidgetStatsTable
                                        items={visibleItems}
                                        labels={widget.labels || {}}
                                        valueType="detailed"
                                        withStatistics={widget.statistic_modal}
                                        onStoreStatsClick={(item) => {
                                            store.set('selectedItem', item);
                                        }}
                                    />
                                )}

                                {filteredItems.length > MAX_VISIBLE_ITEMS && (
                                    <div className={classnames('ppd-widget-compare__expand', {
                                        'ppd-widget-compare__expand--open': isExpanded,
                                    })}
                                    >
                                        <button className="ppd-widget-compare__expand-button" onClick={toggleExpanded}>
                                            {isExpanded ? 'Свернуть' : 'Развернуть'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Модальное окно детального просмотра выбранного элемента магазина */}
            {store.selectedItem && isMobileOrTablet && (
                <PPDWidgetCompareModal
                    item={store.selectedItem} // Выбранный элемент для детального просмотра
                    onClose={() => store.set('selectedItem', null)} // Обработчик закрытия модального окна
                    labels={widget.labels || {}} // Подписи для отображения
                    groups={selectedGroupItems}
                    onChangeGroup={onChangeGroupInStoreModal}
                    groupId={
                        getMonoGroupId(
                            store.selectedItem.store_id,
                            widget.items,
                            groups?.[widget.items_group_type as UserDashboardGroupType] ?? []
                        ) ?? store.groupId
                    }
                    isMonoStore={isMonoStore(
                        store.selectedItem.store_id,
                        widget.items,
                        groups?.[widget.items_group_type as UserDashboardGroupType] ?? []
                    )}
                />
            )}

            <PPDWidgetBonusModal
                isOpen={store.isOpenBonusModal}
                onClose={() => store.set('isOpenBonusModal', false)}
                groups={widget.bonuses?.groups || []}
                items={widget.bonuses?.items || []}
                total={widget.bonuses?.total}
                labels={widget.labels || {}}
            />

            <PPDWidgetStoresModal
                title={widget.stores_modal_title || ''}
                isOpen={store.isOpenStoresModal}
                onClose={() => store.set('isOpenStoresModal', false)}
                items={filteredItems}
                labels={widget.labels || {}}
                groups={store.itemGroups}
                groupId={store.groupId}
                onChangeGroup={(id) => store.set('groupId', id)}
                onSelect={row => store.set('selectedItem', row)}
                stickyHeader={true}
            />
        </>
    );
});
